/**
 * Time grouping utility for follow-ups
 * Groups follow-ups by time periods: Today, Tomorrow, This Week, Later
 */

import { isToday, isTomorrow } from "date-fns";

/**
 * Helper to get start of day
 */
const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Helper to add days
 */
const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Group follow-ups by time periods
 * @param {Array} followUps - Array of follow-up objects
 * @param {boolean} isCompletedTab - Whether this is the completed tab (groups by completion date)
 * @returns {Object} Grouped follow-ups by time period
 */
export const groupByTime = (followUps = [], isCompletedTab = false) => {
  const now = new Date();
  const today = startOfDay(now);
  const tomorrow = startOfDay(addDays(now, 1));
  const weekEnd = addDays(today, 7);

  const groups = {
    today: [],
    tomorrow: [],
    thisWeek: [],
    later: [],
  };

  followUps.forEach((followUp) => {
    if (!followUp.dueDate) {
      groups.later.push(followUp);
      return;
    }

    const dueDate = new Date(followUp.dueDate);
    const dueDateStart = startOfDay(dueDate);
    const isPast = dueDate < now;

    if (isCompletedTab) {
      // For completed items, group by completion/due date
      if (isToday(dueDate)) {
        groups.today.push(followUp);
      } else if (isPast && dueDateStart >= today && dueDateStart < weekEnd) {
        groups.thisWeek.push(followUp);
      } else {
        groups.later.push(followUp);
      }
    } else {
      // For actionable items, group by due date with special handling for overdue
      const isOverdue = followUp.status === "overdue" || (isPast && followUp.status !== "completed");
      
      if (isOverdue || isToday(dueDate)) {
        // Overdue items and today's items go in "today"
        groups.today.push(followUp);
      } else if (isTomorrow(dueDate)) {
        groups.tomorrow.push(followUp);
      } else if (dueDateStart >= today && dueDateStart < weekEnd) {
        groups.thisWeek.push(followUp);
      } else {
        groups.later.push(followUp);
      }
    }
  });

  return groups;
};

